import { Controller } from "@hotwired/stimulus"

const SEVERITY_LABEL = { critical: "CRIT", high: "HIGH", medium: "MED", low: "LOW" }
const SEVERITY_CLASS = { critical: "siem-det-sev--critical", high: "siem-det-sev--high", medium: "siem-det-sev--medium", low: "siem-det-sev--low" }

export default class extends Controller {
  static targets = ["trigger", "panel", "list", "search", "filter"]
  static values = { url: String }

  #open = false
  #boundOutside = null
  #rules = []
  #activeFilter = "all"

  connect() {
    this.#boundOutside = e => {
      if (this.#open && !this.element.contains(e.target)) this.#close()
    }
    document.addEventListener("click", this.#boundOutside)
  }

  disconnect() {
    document.removeEventListener("click", this.#boundOutside)
  }

  toggle() {
    this.#open ? this.#close() : this.#openPanel()
  }

  filterSeverity(event) {
    this.filterTargets.forEach(b => b.classList.remove("active"))
    event.currentTarget.classList.add("active")
    this.#activeFilter = event.currentTarget.dataset.severity || "all"
    this.#renderRules()
  }

  search() {
    this.#renderRules()
  }

  async activate(event) {
    const detectionId = event.currentTarget.dataset.detectionId
    const btn = event.currentTarget
    btn.disabled = true
    btn.textContent = "Activating…"

    try {
      const resp = await fetch(`${this.urlValue}/detections/${detectionId}/activate`, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "",
          Accept: "application/json",
        },
      })
      if (resp.ok) {
        const rule = this.#rules.find(r => r.id === detectionId)
        if (rule) rule.activated = true
        this.#renderRules()
      } else {
        btn.disabled = false
        btn.textContent = "Activate"
      }
    } catch {
      btn.disabled = false
      btn.textContent = "Activate"
    }
  }

  async deactivate(event) {
    const detectionId = event.currentTarget.dataset.detectionId
    const btn = event.currentTarget
    btn.disabled = true
    btn.textContent = "Removing…"

    try {
      const resp = await fetch(`${this.urlValue}/detections/${detectionId}/activate`, {
        method: "DELETE",
        headers: { "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" },
      })
      if (resp.ok) {
        const rule = this.#rules.find(r => r.id === detectionId)
        if (rule) rule.activated = false
        this.#renderRules()
      } else {
        btn.disabled = false
        btn.textContent = "Deactivate"
      }
    } catch {
      btn.disabled = false
      btn.textContent = "Deactivate"
    }
  }

  previewQuery(event) {
    const query = event.currentTarget.dataset.query
    if (!query) return
    const textarea = document.querySelector("textarea[name='query']")
    if (!textarea) return
    textarea.value = query
    textarea.dispatchEvent(new Event("input", { bubbles: true }))
    this.#close()
  }

  async #openPanel() {
    this.#open = true
    this.panelTarget.classList.remove("hidden")
    this.triggerTarget.classList.add("active")
    await this.#loadRules()
  }

  #close() {
    this.#open = false
    this.panelTarget.classList.add("hidden")
    this.triggerTarget.classList.remove("active")
  }

  async #loadRules() {
    this.listTarget.innerHTML = '<div class="siem-det-loading"><i class="fa fa-spin fa-spinner"></i> Loading detections…</div>'
    try {
      const resp = await fetch(`${this.urlValue}/detection_library`, { headers: { Accept: "application/json" } })
      this.#rules = await resp.json()
      this.#renderRules()
    } catch {
      this.listTarget.innerHTML = '<div class="siem-det-empty">Failed to load detection library.</div>'
    }
  }

  #renderRules() {
    const query = this.hasSearchTarget ? this.searchTarget.value.toLowerCase() : ""
    const filtered = this.#rules.filter(r => {
      if (this.#activeFilter !== "all" && r.severity !== this.#activeFilter) return false
      if (query && !r.name.toLowerCase().includes(query) && !r.description.toLowerCase().includes(query)) return false
      return true
    })

    if (!filtered.length) {
      this.listTarget.innerHTML = '<div class="siem-det-empty">No detections match your filter.</div>'
      return
    }

    this.listTarget.innerHTML = filtered.map(r => {
      const sevClass = SEVERITY_CLASS[r.severity] || "siem-det-sev--medium"
      const sevLabel = SEVERITY_LABEL[r.severity] || r.severity
      const mitre = (r.mitre || []).map(t => `<span class="siem-det-mitre">${this.#esc(t)}</span>`).join("")
      const actionBtn = r.activated
        ? `<button class="btn btn-xs btn-default siem-det-deactivate" data-detection-id="${r.id}" data-action="click->accounts--logs--detection-library#deactivate">Deactivate</button>`
        : `<button class="btn btn-xs btn-warning siem-det-activate" data-detection-id="${r.id}" data-action="click->accounts--logs--detection-library#activate">Activate</button>`
      return `
        <div class="siem-det-rule${r.activated ? " siem-det-rule--active" : ""}">
          <div class="siem-det-header">
            <span class="siem-det-sev ${sevClass}">${sevLabel}</span>
            <span class="siem-det-name">${this.#esc(r.name)}</span>
          </div>
          <p class="siem-det-desc">${this.#esc(r.description)}</p>
          <div class="siem-det-footer">
            <div class="siem-det-tags">${mitre}</div>
            <div class="siem-det-actions">
              <button class="btn btn-xs btn-link siem-det-preview" data-query="${this.#esc(r.query)}" data-action="click->accounts--logs--detection-library#previewQuery" title="Load query in search bar">
                <i class="fa fa-search"></i> Preview
              </button>
              ${actionBtn}
            </div>
          </div>
        </div>
      `
    }).join("")
  }

  #esc(str) {
    return String(str)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;")
      .replace(/>/g, "&gt;").replace(/"/g, "&quot;")
  }
}
